import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldCheck, Info, Fire, Timer, Gauge } from "@phosphor-icons/react";

const PRESETS = [
  { name: "Conservador", desc: "Máxima proteção. Ideal pra número novo ou muito valioso.", cfg: { max_per_minute: 8, max_per_hour: 200, min_delay_seconds: 4, max_delay_seconds: 10, warmup_hours: 72, warmup_max_per_hour: 20 } },
  { name: "Balanceado", desc: "Padrão recomendado pra maioria dos negócios.", cfg: { max_per_minute: 20, max_per_hour: 500, min_delay_seconds: 2, max_delay_seconds: 5, warmup_hours: 48, warmup_max_per_hour: 30 } },
  { name: "Agressivo", desc: "Mais volume, mais risco. Só use com número aquecido.", cfg: { max_per_minute: 40, max_per_hour: 1500, min_delay_seconds: 1, max_delay_seconds: 3, warmup_hours: 24, warmup_max_per_hour: 60 } },
];

export default function CompanyAntiBan() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/anti-ban");
      setSettings(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const applyPreset = (cfg) => setSettings((s) => ({ ...s, ...cfg }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        max_per_minute: Number(settings.max_per_minute),
        max_per_hour: Number(settings.max_per_hour),
        min_delay_seconds: Number(settings.min_delay_seconds),
        max_delay_seconds: Number(settings.max_delay_seconds),
        warmup_hours: Number(settings.warmup_hours),
        warmup_max_per_hour: Number(settings.warmup_max_per_hour),
        typing_indicator: !!settings.typing_indicator,
        read_receipts: !!settings.read_receipts,
      };
      const { data } = await api.put("/anti-ban", payload);
      setSettings(data);
      toast.success("Anti-ban atualizado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-8 text-sm text-[#5C5C59]">Carregando…</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="anti-ban-page">
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Segurança</p>
        <h1 className="text-4xl font-black tracking-tighter mt-1 flex items-center gap-2">
          <ShieldCheck size={32} weight="duotone" /> Anti-ban
        </h1>
        <p className="text-sm text-[#5C5C59] mt-2">
          Controla o ritmo dos envios pra proteger o WhatsApp do cliente. Aplicado em todas as conexões da empresa.
        </p>
      </div>

      {/* Presets */}
      <section className="mb-6">
        <p className="text-[10px] uppercase font-semibold tracking-wider text-[#5C5C59] mb-2">Presets rápidos</p>
        <div className="grid md:grid-cols-3 gap-3">
          {PRESETS.map((p) => (
            <button
              key={p.name}
              onClick={() => applyPreset(p.cfg)}
              data-testid={`preset-${p.name.toLowerCase()}`}
              className="text-left p-4 border-2 border-[#E5E5E0] hover:border-[#0F4C3A] bg-white transition-colors"
            >
              <p className="font-black">{p.name}</p>
              <p className="text-xs text-[#5C5C59] mt-1">{p.desc}</p>
              <p className="text-[10px] font-mono text-[#0F4C3A] mt-2">
                {p.cfg.max_per_minute}/min · {p.cfg.max_per_hour}/h
              </p>
            </button>
          ))}
        </div>
      </section>

      {/* Manual config */}
      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-4">
        <h2 className="text-lg font-black mb-4 flex items-center gap-2"><Gauge size={20} /> Limites de envio</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Máx. por minuto</Label>
            <Input data-testid="input-max-per-minute" type="number" min="1" max="120" value={settings.max_per_minute}
              onChange={(e) => setField("max_per_minute", e.target.value)} className="rounded-none mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Máx. por hora</Label>
            <Input data-testid="input-max-per-hour" type="number" min="10" max="5000" value={settings.max_per_hour}
              onChange={(e) => setField("max_per_hour", e.target.value)} className="rounded-none mt-1 font-mono" />
          </div>
        </div>
      </section>

      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-4">
        <h2 className="text-lg font-black mb-4 flex items-center gap-2"><Timer size={20} /> Delay entre mensagens</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Mín. (segundos)</Label>
            <Input data-testid="input-min-delay" type="number" min="0" max="30" value={settings.min_delay_seconds}
              onChange={(e) => setField("min_delay_seconds", e.target.value)} className="rounded-none mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Máx. (segundos)</Label>
            <Input data-testid="input-max-delay" type="number" min="0" max="60" value={settings.max_delay_seconds}
              onChange={(e) => setField("max_delay_seconds", e.target.value)} className="rounded-none mt-1 font-mono" />
          </div>
        </div>
        <p className="text-xs text-[#5C5C59] mt-3">
          O sistema espera um tempo aleatório entre esses valores antes de cada envio, simulando digitação humana.
        </p>
      </section>

      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-4">
        <h2 className="text-lg font-black mb-4 flex items-center gap-2"><Fire size={20} weight="duotone" /> Modo aquecimento</h2>
        <p className="text-sm text-[#5C5C59] mb-4">
          Durante as primeiras horas de uma nova conexão, os limites são reduzidos automaticamente pra WhatsApp não estranhar volume súbito.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Duração (horas)</Label>
            <Input data-testid="input-warmup-hours" type="number" min="0" max="240" value={settings.warmup_hours}
              onChange={(e) => setField("warmup_hours", e.target.value)} className="rounded-none mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Máx. por hora no fim do aquecimento</Label>
            <Input data-testid="input-warmup-max" type="number" min="5" max="500" value={settings.warmup_max_per_hour}
              onChange={(e) => setField("warmup_max_per_hour", e.target.value)} className="rounded-none mt-1 font-mono" />
          </div>
        </div>
      </section>

      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-6">
        <h2 className="text-lg font-black mb-4">Sinais humanos</h2>
        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 bg-[#F7F7F5] cursor-pointer">
            <span>
              <span className="font-semibold text-sm">&quot;Digitando…&quot;</span>
              <p className="text-xs text-[#5C5C59]">Envia indicador de digitação antes da mensagem.</p>
            </span>
            <Switch checked={!!settings.typing_indicator} onCheckedChange={(v) => setField("typing_indicator", v)} data-testid="switch-typing" />
          </label>
          <label className="flex items-center justify-between p-3 bg-[#F7F7F5] cursor-pointer">
            <span>
              <span className="font-semibold text-sm">Marcar msgs recebidas como lidas</span>
              <p className="text-xs text-[#5C5C59]">Manda o &quot;visto&quot; antes de responder — comportamento natural.</p>
            </span>
            <Switch checked={!!settings.read_receipts} onCheckedChange={(v) => setField("read_receipts", v)} data-testid="switch-read" />
          </label>
        </div>
      </section>

      <div className="p-4 bg-[#EDF3F0] border-l-[3px] border-[#0F4C3A] text-xs mb-6 flex gap-2">
        <Info size={16} weight="duotone" className="text-[#0F4C3A] flex-shrink-0 mt-0.5" />
        <div>
          Os limites protegem o número do cliente, mas <b>nada substitui bom senso</b>: evite disparos em massa, use varilingua, respeite opt-out. Se o cliente pedir pra parar, pare.
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="anti-ban-save"
          className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white px-8 py-6">
          {saving ? "Salvando…" : "Salvar anti-ban"}
        </Button>
      </div>
    </div>
  );
}
